import { PageHelpContent } from '../components/Common/PageHelpModal';
import { PNT_DESCRIPTION, PNT_LONG } from '../constants/pnt';
import { OPS_SURFACE_LABELS } from '../config/opsSurfaceLabels';
import { OPS_RISK_PRIORITY_BASES, OPS_RISK_SCORE_CAP } from '../lib/ops/riskBases';

/**
 * Centralized help content registry for all pages
 * Keys are route paths, values are PageHelpContent objects
 */
export const PAGE_HELP_CONTENT: Record<string, PageHelpContent> = {
  // ==================== UCC (Unified Communications Center) ====================
  '/ucc': {
    title: 'UCC — Unified Communications Center',
    summary:
      'UCC is the central inbox for customer conversations, replacing Freshdesk. During parallel-run, tickets flow bidirectionally between UCC and FD every 30 minutes; replies sent from UCC push to FD immediately.',
    sections: [
      {
        title: 'What you see on a ticket',
        content:
          'Every ticket has a status/priority pill, a source badge (UCC Native or FD Mirror), linked customer + PNR + call history in the right sidebar, and AI-proposed enrichments at the top of the sidebar.',
        steps: [
          'FD Mirror tickets are authoritative in Freshdesk — your reply pushes to FD automatically.',
          'UCC Native tickets live here only unless admin has enabled UCC→FD ticket creation.',
          'Customer, PNR, and call-log widgets appear automatically when the FK is populated.',
          'AI suggestions (link customer, link PNR, add tag, change priority) require explicit Accept.'
        ]
      },
      {
        title: 'Keyboard shortcuts',
        content: 'Press ? anywhere on a ticket to see the full list. Key ones:',
        steps: [
          'J / K — next / previous ticket in the list',
          'R — focus reply composer',
          'N — switch composer to internal note',
          'Esc — clear selection / close modal / back to list',
          'Cmd (or Ctrl) + K — command palette, with inline ticket search by #id or subject',
          '? — this help plus the full shortcut reference'
        ]
      },
      {
        title: 'Finding tickets elsewhere in the platform',
        content:
          'UCC tickets surface in other modules via the same FK links:',
        steps: [
          'CRM customer detail → Communications tab',
          'PNR Record page → UCC Tickets card below Remarks',
          'Company detail → tickets from every contact belonging to the company',
          'Client360 overview → tickets summary',
          'Freshdesk dashboard → UCC column with one-click jump to the mirror'
        ]
      }
    ],
    tips: [
      'Bulk close/assign/merge: select multiple rows in the list, then use the toolbar (or press E to close).',
      'AI Suggestions uses OpenRouter (same key chain as other platform AI features). It never writes — only proposes.',
      'Admin can flip reconciler mode (dry-run ↔ write) at Settings → Communications → FD Sync.'
    ]
  },
  '/ucc/new': {
    title: 'Create a new UCC ticket',
    summary:
      'Raise a ticket from scratch. Pre-populated from the query string when opened from a customer or PNR — e.g. "+ New ticket" on a CRM customer page locks the customer_id.',
    sections: [
      {
        title: 'Before creating',
        content:
          'The display_id is assigned by the server sequence when you save — you do not pick it. UCC-native tickets stay in UCC unless FD-sync push is enabled.'
      }
    ],
    tips: [
      'If you typed a PNR record locator, UCC validates it against pnr_records and links booking_id automatically.'
    ]
  },
  '/ops/inbox': {
    title: 'Ops Inbox — Legend & Quick Reference',
    summary:
      'Unified work queue across DK action items, WhatsApp sweeps, Freshdesk scans, Gmail sweeps, FD tickets, PNRs, PNTs, AOC alerts, and manual entries. Items are ranked by priority + risk score (0–100) computed from due-date proximity. Filters apply across sources; selecting a row opens a detail sheet with AI assist and reply composer.',
    sections: [
      {
        title: 'Acronyms',
        content: 'The terms you will see across the cockpit:',
        steps: [
          'UCC — Unified Communications Center, the native PT inbox replacing Freshdesk.',
          'FD — Freshdesk; legacy ticketing. Mirror tickets are FD-origin and reply via UCC auto-pushes to FD.',
          'UCC Native — ticket created in UCC; stays UCC-only unless admin enables UCC→FD push.',
          'PNR — Passenger Name Record (Sabre / Amadeus / NDC).',
          'PNT — Propeller Ticketing — internal ticketing/refund/MCO records.',
          'AOC — Airline Operations Center; disruption / IRROPS alerts.',
          'DK / DK-AI — Daniel Krupka action items, sourced from DK Dashboard Inbox.',
          'CRM — customer / traveller / contact records.',
          'Cowork — Claude Cowork queue; time-gated responder used for WhatsApp + risky actions.',
        ],
      },
      {
        title: 'Source pills (icon + colour)',
        content: 'Each work item is tagged with one of these source pills:',
        steps: [
          'WhatsApp (emerald, MessageSquare) — item from a WhatsApp sweep.',
          'FD scan (green, Mail) — Freshdesk inbound scanned by cron.',
          'Gmail (red, Mail) — Gmail thread sweep.',
          'Freshdesk (lime, Mail) — Freshdesk ticket.',
          'PNR (sky, Plane) — PNR record needing attention.',
          'PNT (amber, Ticket) — PNT transaction.',
          'AOC (violet, Radio) — AOC disruption alert.',
          'DK-AI (slate, Bot) — DK action item.',
          'Manual (gray, Inbox) — manually created.',
          'Safe test (cyan, ClipboardCheck) — synthetic smoke-test item; no customer is contacted.',
        ],
      },
      {
        title: 'Priority levels and risk score',
        content: 'Rows are coloured by priority on their left bar, and ordered within a priority bucket by risk score:',
        steps: [
          `CRITICAL (red) — risk base ${OPS_RISK_PRIORITY_BASES.CRITICAL}.`,
          `URGENT (rose) — risk base ${OPS_RISK_PRIORITY_BASES.URGENT}.`,
          `HIGH (orange) — risk base ${OPS_RISK_PRIORITY_BASES.HIGH}.`,
          `MEDIUM (blue) — risk base ${OPS_RISK_PRIORITY_BASES.MEDIUM}.`,
          `LOW (gray) — risk base ${OPS_RISK_PRIORITY_BASES.LOW}.`,
          `Risk score = priority base + due-date proximity bonus (overdue +12, due within 6 hours +10, within 24 hours +7, within 72 hours +3), capped at ${OPS_RISK_SCORE_CAP}. Higher = more urgent.`,
          `Worked example: a HIGH item due in 5 hours scores ${OPS_RISK_PRIORITY_BASES.HIGH} + 10 = ${OPS_RISK_PRIORITY_BASES.HIGH + 10}; a LOW item with no due date scores ${OPS_RISK_PRIORITY_BASES.LOW}.`,
        ],
      },
      {
        title: 'AI assist modes',
        content: 'Inside an item the AI panel offers five modes (Claude via OpenRouter):',
        steps: [
          'Brief (Sparkles, Sonnet 4.6) — one-paragraph operational summary.',
          'Cross-check (CheckCircle2, Sonnet 4.6) — verifies CRM / risk / promise consistency.',
          'Draft (Wand2, Sonnet 4.6) — customer-facing reply draft.',
          'Why priority (Brain, Sonnet 4.6) — justifies the calculated priority.',
          'Escalate (AlertTriangle, Opus 4.7) — full escalation memo for risky cases.',
        ],
      },
      {
        title: 'Reply channels',
        content: 'The reply composer auto-picks the right channel:',
        steps: [
          'UCC — native send. For FD-mirror tickets the platform auto-pushes the same reply to Freshdesk.',
          'FD — only available when the ticket exists in Freshdesk but not in UCC. Hidden when UCC is available, to prevent double-sending.',
          'WhatsApp — queued through Cowork; only fires inside the operating window (London 7:00–00:59).',
        ],
      },
      {
        title: 'Keyboard shortcuts',
        content: 'Most queue work can be done without the mouse:',
        steps: [
          'J / K — next / previous item.',
          'Enter / O — open the selected item in the detail sheet.',
          '? — toggle this Legend.',
          'Esc — close the detail sheet.',
        ],
      },
      {
        title: 'Bulk actions',
        content: 'Tick the checkbox at the start of each row to multi-select. A floating action bar appears at the bottom with Complete, Snooze 2h, Dismiss, and Claim. Each action is run sequentially against the selected items.',
      },
      {
        title: 'Deep-linking',
        content: 'When an item is open the URL is updated to /ops/inbox?item=<key>, so you can paste it into Slack and a colleague will land directly on the same case.',
      },
      {
        title: 'Saved views',
        content: 'Set your filters (priorities, sources, search, date range, snoozed/completed toggles) and click the Views button to save the current combination by name. Re-open it from the same dropdown later. Views are per-user. The date range and Custom From/To round-trip with the view.',
      },
      {
        title: 'Date range filter',
        content: 'Limit the queue by when an item appeared (its created_at):',
        steps: [
          'Anytime — no restriction (default).',
          'Last 7 days — items created in the last week.',
          'Last month — items created in the last 30 days.',
          'Last 3 months — items created in the last 90 days.',
          'Custom — pick From / To dates explicitly. UTC days; inclusive on both ends.',
        ],
      },
      {
        title: 'KPI strip',
        content: 'Above the list you will see Open / Critical / Overdue / Oldest / Completed-24h / Dismissed-24h / Active-agents counts. Refreshes every five minutes.',
      },
      {
        title: 'Cowork status strip',
        content: 'The four-pill strip under the header shows live operating state:',
        steps: [
          'Cowork window — active when London 7:00–00:59. Outside the window the responder is quiet.',
          'WhatsApp sweep — time since the last sweep ran.',
          'Pending actions — Cowork queue depth and stale count (≥90 minutes waiting).',
          'Instant UCC send — UCC reply preflight status. If "Needs attention", public sends are blocked but private notes still work.',
        ],
      },
    ],
    tips: [
      'FD sync pill in the header shows reconciler state (paused / dry-run / writing). Click to open Settings → Communications.',
      'Test mode toggles a synthetic Ops item so you can practice without contacting a customer.',
      'If an AI call fails, read the message before assuming an outage. "Not configured" or "no key" means the OpenRouter or OpenAI key is missing (Settings → Integrations); an error while a key is present means the provider or network failed, so retry once and then report it.',
    ],
  },
  '/ops/dispatch': {
    title: 'Ops Dispatch — Legend & Quick Reference',
    summary:
      'Supervisor view of canonical cases by person. It decides who owns what and in which order each operator works it. It is not the intake list: new observations are reviewed in Ops Inbox first.',
    sections: [
      {
        title: 'How each person stack is ordered',
        content: 'Cases in a person stack are shown in this order:',
        steps: [
          'Working state — a case someone is actively working comes first.',
          'Manager sequence — the exact Next order a supervisor set for that operator.',
          'Priority — CRITICAL, URGENT, HIGH, MEDIUM, LOW.',
          'Due — earlier due time first; a case with no due time shows "Missing instruction".',
          'Last update — the most recently updated case breaks any remaining tie.',
        ],
      },
      {
        title: 'What the counts mean',
        content: 'Counts describe the loaded projection, not every case in the company:',
        steps: [
          'Live cases loaded — up to 200 highest-priority live cases; it is not a full total.',
          'Source triage — unresolved source observations that have not become cases.',
          'Verification lane — submissions awaiting manager review; it is not the same as an imported or recorded verification status shown on a card.',
          '"Collapsed" / "Open" on a section header always reflects whether that section is open right now.',
        ],
      },
      {
        title: 'Other intake and diagnostics',
        content: 'The shared capped intake, source review and system diagnostics stay available in the section at the foot of the page. They do not represent a complete person-by-person workload.',
      },
    ],
    tips: [
      'Opening a case from here never changes its owner; use the assignment controls for that.',
      'The risk score and bases used by Ops Inbox are explained in the Ops Inbox legend.',
    ],
  },
  '/settings/communications': {
    title: 'UCC Settings',
    summary:
      'Admin-only. Manage ticket statuses, automation rules, SLA policies, and the Freshdesk sync reconciler.',
    sections: [
      {
        title: 'FD Sync tab',
        content:
          'Operates the 30-min reconciler: dry-run / write mode, pause kill switch, UCC→FD ticket creation gate, last-20 run history with expandable errors, manual dry-run / write triggers with a backfill window.',
        steps: [
          'Start in dry-run. Watch the first few runs.',
          'Flip to write mode when the deltas look sane.',
          'create_freshdesk_tickets=false during parallel-run so we do not duplicate tickets in FD.',
          'Pause clears no cursor — safe to resume at any time.'
        ]
      }
    ]
  },

  '/ai/amadeus-agent': {
    title: 'Amadeus AI Agent',
    summary:
      'A chat box for the Amadeus (1A) system. Type a cryptic command, a record locator or a short request; it runs the command in Amadeus and shows what Amadeus answered.',
    sections: [
      {
        title: 'What you can type',
        content: 'The agent recognises a few forms. Anything else gets a reminder to use a cryptic command, not a guess.',
        steps: [
          'A cryptic command that starts with AN, RT, SS or FX is sent to Amadeus as written.',
          'A record locator (for example "Retrieve PNR ABC123") is retrieved with RT.',
          '"Price PNR ABC123 and create a TST" prices the booking and creates the TST(s) in one session. It does not issue anything.',
        ],
      },
      {
        title: 'What it does not do',
        content: 'It runs one command at a time and shows the reply. It does not book, ticket or send anything to a client unless you type the command that does. Check the reply before relying on it.',
      },
    ],
    relatedPages: [
      { name: 'Sabre AI Agent', path: '/ai/sabre-agent' },
    ],
    tips: [
      'If the reply says "Error executing command", Amadeus did not answer. Nothing was changed by that attempt.',
    ],
  },

  // ==================== DEFAULT & DOMAIN PRIMERS ====================
  'default': {
    title: 'Propeller Travel Platform - 5-Domain Guide',
    summary: 'Propeller Platform is organized into 5 top-level operational domains: Operations, CRM, Finance, Intelligence, and Admin. Use Ctrl+K (or Cmd+K) from anywhere to search all pages, customers, PNRs, trips, invoices, and manuals.',
    sections: [
      {
        title: '5-Domain Navigation Taxonomy',
        content: 'Switch between the five domain tabs at the top of the left sidebar to access specialized workspaces:',
        steps: [
          'Operations: Daily Command (Today, Ops Inbox, AOC, UCC), Fulfilment (PNR, PNT, Bookings, Trips, Cruises, RTW), and GDS Queues',
          'CRM: Customers, Corporate Companies, Travelers, Suppliers 360, Hotels, Partners, Leads, and Document Vault',
          'Finance: Invoices, Stripe Payments, Plaid Banking, AR Aging, Commissions, Supplier AP Invoices, and Fora Sync',
          'Intelligence: Executive & Operational Reports, Agent Performance, AI Copilot, and OpenRouter AI Usage',
          'Admin: User & Bot Accounts, Role Permissions, Automation Control Center, Integrations Hub, System Health, and System Manual'
        ]
      },
      {
        title: 'Troubleshooting & System Architecture Flowcharts',
        content: 'If data appears stale or an external integration fails, inspect the live health banners or open the System Manual for end-to-end "What Talks to What" architecture flowcharts.',
        steps: [
          'Check Ops Control Tower (/ops/control-tower) or System Health (/admin/system-health) for poller and webhook status',
          'Open System Manual (/admin/system-manual) to view interactive Mermaid architecture and data-flow diagrams',
          'Press Ctrl+K / Cmd+K to jump directly to any route or record'
        ]
      }
    ],
    relatedPages: [
      { name: 'System Manual & Flowcharts', path: '/admin/system-manual' },
      { name: 'Staff Manuals', path: '/manuals' },
      { name: 'Ops Handbook', path: '/ops/handbook' },
      { name: 'System Health', path: '/admin/system-health' }
    ],
    tips: [
      'Press Cmd/Ctrl+K to open the unified Master Search Palette across all 5 domains',
      'Click any parent segment in the top breadcrumb bar to navigate up the hierarchy'
    ]
  },
  'domain:operations': {
    title: 'Operations Domain Guide & Troubleshooting',
    summary: 'Manage live traveller disruptions (AOC), omnichannel tickets (UCC), GDS PNRs, Paid-Not-Ticketed (PNT) queues, Trips, Cruises, and RTW itineraries.',
    sections: [
      {
        title: 'How Operations Systems Talk to Each Other',
        content: 'Sabre/Amadeus PNRs sync into Bookings, Trips, PNT, and Ticketing Deadlines. Freshdesk, Email, and WhatsApp threads converge in UCC and link directly to Customer 360 and Active Trips.',
        steps: [
          'Start morning triage in Today (/ops/today) and Ops Inbox (/ops/inbox)',
          'Check PNT (/pnt) and Ticketing Deadlines (/bookings/deadlines) for urgent ticketing SLAs',
          'Use RTW Planner (/rtw) Step 1 to build routes and Step 2 to validate Oneworld Rule 3015 compliance'
        ]
      },
      {
        title: 'Troubleshooting Operations',
        content: 'Common fixes when bookings, segments, or tickets do not appear as expected:',
        steps: [
          'HX / Cancelled Segments: Use the HX cleanup actions on Trips (/trips) or PNR Manager (/pnr) to strip inactive GDS segments',
          'Missing Freshdesk / Email Thread: Check UCC Settings (/settings/communications) or Freshdesk Sync (/admin/fd-import)',
          'Queue Stalls: Check Queue Monitor (/automation/queues) and Ops Control Tower (/ops/control-tower)'
        ]
      }
    ],
    relatedPages: [
      { name: 'System Manual & Flowcharts', path: '/admin/system-manual' },
      { name: 'Ops Handbook', path: '/ops/handbook' },
      { name: 'Ops Control Tower', path: '/ops/control-tower' }
    ],
    tips: [
      'Trips (/trips) hides test trips by default; use the "N test trips hidden (show)" / "Hide test trips" toggle to include or hide them',
      'Click any PNR locator pill to copy the 6-character record locator'
    ]
  },
  'domain:crm': {
    title: 'CRM Domain Guide & Troubleshooting',
    summary: 'Single source of truth for Customers, Corporate Companies, Travelers, Canonical Supplier 360 Records, Preferred Hotels, Partners, and Document Vault.',
    sections: [
      {
        title: 'Customer & Supplier 360 Architecture',
        content: 'Customer records aggregate linked Travelers, Trips, UCC Tickets, Invoices, and Documents. Supplier records unify CRM vendor contacts, AP invoices, and commission rules.',
        steps: [
          'Use Customers (/crm/customers) for client cockpits and Contact Matching (/crm/contact-matching) to resolve duplicates',
          'Use Suppliers (/crm/suppliers) as the canonical vendor directory across CRM, Operations, and Finance',
          'Store verified passports, visas, and contracts in Document Vault (/crm/documents)'
        ]
      },
      {
        title: 'Troubleshooting CRM Records',
        content: 'Resolving unmatched contacts or missing linked records:',
        steps: [
          'Unlinked UCC ticket or invoice: Run Contact Matching (/crm/contact-matching) or link the email on the Customer profile',
          'Bot / QA clutter: Filter out synthetic test profiles using the search and segment filters'
        ]
      }
    ],
    relatedPages: [
      { name: 'Customers', path: '/crm/customers' },
      { name: 'Suppliers 360', path: '/crm/suppliers' },
      { name: 'Contact Matching', path: '/crm/contact-matching' },
      { name: 'System Manual & Flowcharts', path: '/admin/system-manual' }
    ]
  },
  'domain:finance': {
    title: 'Finance Domain Guide & Troubleshooting',
    summary: 'End-to-end financial control across Client Invoices, Stripe Payments, Plaid Bank Feeds, AR Aging, Commissions, Supplier AP Invoices, and Fora Sync.',
    sections: [
      {
        title: 'Financial Data Flow (What Talks to What)',
        content: 'Stripe payment intents and Plaid bank transactions reconcile against Client Invoices and PNT ticketing readiness. Gmail AP Pollers ingest supplier PDF invoices into Supplier Invoices (/finance/supplier-invoices).',
        steps: [
          'Track client receivables in Invoices (/invoices) and AR Aging (/finance/ar-aging)',
          'Verify card settlements in Payments (/payments) and bank feeds in Banking (/banking)',
          'Review ingested vendor bills in Supplier Invoices (/finance/supplier-invoices) and advisor payouts in Commissions (/commissions)'
        ]
      },
      {
        title: 'Troubleshooting Finance & Reconciliation',
        content: 'When payments, bank feeds, or supplier invoices need attention:',
        steps: [
          'Plaid bank feed stale: Click Re-authenticate or Sync on Banking (/banking) or check Integrations Hub (/integrations)',
          'Supplier PDF failed extraction: Use the manual review drawer in Supplier Invoices (/finance/supplier-invoices) or check Supplier Pollers (/admin/supplier-pollers)',
          'Stripe account separation: UK Stripe account and US Stripe account are strictly separated, so verify the account badge before action'
        ]
      }
    ],
    relatedPages: [
      { name: 'Finance Dashboard', path: '/finance' },
      { name: 'Supplier Invoices', path: '/finance/supplier-invoices' },
      { name: 'Banking', path: '/banking' },
      { name: 'System Manual & Flowcharts', path: '/admin/system-manual' }
    ]
  },
  'domain:intelligence': {
    title: 'Intelligence Domain Guide & Troubleshooting',
    summary: 'Executive analytics, Agent Performance leaderboards, AI Copilot workflows, and OpenRouter model usage/cost telemetry.',
    sections: [
      {
        title: 'Analytics & AI Telemetry',
        content: 'Intelligence aggregates verified booking, margin, SLA, and AI token telemetry across the platform.',
        steps: [
          'Use Reports (/reports) and Agent Performance (/analytics/agent-performance) for revenue, conversion, and SLA metrics',
          'Use AI Assistant (/ai-assistant) for itinerary drafting, fare rule lookups, and case summaries',
          'Monitor LLM token spend and provider latency in AI Usage Stats (/ai/stats)'
        ]
      }
    ],
    relatedPages: [
      { name: 'Reports', path: '/reports' },
      { name: 'Agent Performance', path: '/analytics/agent-performance' },
      { name: 'AI Usage Stats', path: '/ai/stats' },
      { name: 'System Manual & Flowcharts', path: '/admin/system-manual' }
    ]
  },
  'domain:admin': {
    title: 'Admin Domain Guide & Troubleshooting',
    summary: 'Centralized governance for Users & Bots, RBAC Permissions, Automation Control Center, Third-Party Integrations Hub, System Health, and System Manuals.',
    sections: [
      {
        title: 'Centralized Credentials & Automation Control',
        content: 'All third-party API credentials, webhook health checks, and background cron schedules are centrally governed in Admin and referenced by contextual shortcuts across domain pages.',
        steps: [
          'Manage Staff, Client, and Bot/QA accounts in Users (/admin/users) and route access in Permissions (/admin/permissions)',
          'Inspect and toggle scheduled jobs in Automations (/admin/automations)',
          'Manage API keys and connection health in Integrations (/integrations) and System Health (/admin/system-health)'
        ]
      }
    ],
    relatedPages: [
      { name: 'System Manual & Flowcharts', path: '/admin/system-manual' },
      { name: 'Integrations Hub', path: '/integrations' },
      { name: 'Automation Control Center', path: '/admin/automations' },
      { name: 'System Health', path: '/admin/system-health' }
    ]
  },

  // ==================== DASHBOARD ====================
  '/': {
    title: 'Home Dashboard',
    summary: 'Your central hub showing key metrics, recent activity, and quick access to common tasks. Monitor your workload at a glance.',
    sections: [
      {
        title: 'Dashboard Widgets',
        content: 'The dashboard displays various widgets showing your current workload and important metrics.',
        steps: [
          'Smart Queue widget shows pending tasks by priority',
          'Revenue widget tracks sales performance',
          'Customer Locations map shows where travelers are currently located',
          'Recent Activity shows latest bookings and updates'
        ]
      },
      {
        title: 'Customizing Your Dashboard',
        content: 'Widgets can be rearranged and configured based on your role and preferences.'
      }
    ],
    relatedPages: [
      { name: 'Customer Locations', path: '/dashboard/locations' },
      { name: 'Smart Queue', path: '/operations/queue' }
    ]
  },

  '/dashboard/locations': {
    title: 'Customer Locations',
    summary: 'Track where your customers are currently located based on their active itineraries. Monitor travelers in real-time and identify potential disruptions.',
    sections: [
      {
        title: 'Understanding the Map',
        content: 'The map shows markers for customers who are currently traveling.',
        steps: [
          'Blue markers: Customers at their destination',
          'Yellow markers: Customers in transit',
          'Red markers: Customers with potential issues (delays, disruptions)',
          'Click a marker to see customer details'
        ]
      },
      {
        title: 'Risk Monitoring',
        content: 'The system automatically monitors for weather, strikes, or other events that could affect travelers.'
      }
    ],
    tips: [
      'Filter by risk level to focus on customers who may need assistance',
      'Export the list to contact travelers proactively'
    ]
  },

  // ==================== QUEUE MANAGEMENT ====================
  '/automation/queues': {
    title: 'Queue Dashboard',
    summary: 'Configure which Sabre queues to monitor automatically. Set up rules for auto-classification, routing, and notifications.',
    sections: [
      {
        title: 'Adding Queue Watches',
        content: 'Create queue watch configurations to automatically monitor specific Sabre queues.',
        steps: [
          'Click "Add Queue" to create a new watch',
          'Enter the queue number (e.g., 1, 7, 25) and category',
          'Configure auto-pull to automatically retrieve items',
          'Set auto-classify to categorize items by type',
          'Enable auto-route to send items to Freshdesk groups'
        ]
      },
      {
        title: 'Monitoring Status',
        content: 'View the status of each queue watch including last check time, pending items, and any errors.',
        steps: [
          'Green status: Queue is being monitored normally',
          'Yellow status: Queue has pending items requiring attention',
          'Red status: Queue has errors or hasn\'t been checked recently'
        ]
      },
      {
        title: 'Using Ingest to Tracking',
        content: 'Click "Ingest to Tracking" to pull all queue items into the system for processing and tracking.'
      }
    ],
    relatedPages: [
      { name: 'Queue Actions', path: '/automation/queue-actions' },
      { name: 'Schedule Changes', path: '/automation/schedule-changes' }
    ],
    tips: [
      'Start with the standard queues: 1 (Ticketing), 7 (Schedule Changes), 25 (General)',
      'Enable email notifications for critical queues'
    ]
  },

  '/automation/queue-actions': {
    title: 'Queue Actions',
    summary: 'Work through indexed PNRs from your queues. This is your main action worklist showing all items requiring attention.',
    sections: [
      {
        title: 'Working the Queue',
        content: 'PNRs are displayed with priority indicators and required actions.',
        steps: [
          'Filter by priority (Critical, High, Medium, Low) to focus on urgent items',
          'Filter by status to see Pending, In Progress, or Completed items',
          'Click on a PNR to see full details and take action',
          'Mark items as completed when finished'
        ]
      },
      {
        title: 'Priority Levels',
        content: 'Items are prioritized based on departure date, ticketing deadlines, and queue type.',
        steps: [
          'Critical (Red): Departing within 24 hours or ticketing deadline imminent',
          'High (Orange): Departing within 72 hours or schedule changes',
          'Medium (Blue): Standard processing required',
          'Low (Gray): Non-urgent items'
        ]
      },
      {
        title: 'Bulk Actions',
        content: 'Select multiple items to assign to an agent or mark as completed in bulk.'
      }
    ],
    relatedPages: [
      { name: 'Queue Dashboard', path: '/automation/queues' },
      { name: 'Smart Queue', path: '/operations/queue' }
    ]
  },

  '/automation/schedule-changes': {
    title: 'Schedule Changes',
    summary: 'Review and action airline schedule changes affecting your bookings. Compare fare impacts and decide whether to accept or rebook.',
    sections: [
      {
        title: 'Reviewing Schedule Changes',
        content: 'Each schedule change shows the original and new flight details with fare impact.',
        steps: [
          'Review the change type (time change, flight cancellation, etc.)',
          'Compare original fare vs new fare',
          'Check the system recommendation (keep current or rebook)',
          'Approve to accept the change or Reject to explore alternatives'
        ]
      },
      {
        title: 'Fare Impact Analysis',
        content: 'The system calculates fare differences to help you make informed decisions.',
        steps: [
          'Green: New fare is lower or same',
          'Yellow: Small fare increase (under 10%)',
          'Red: Significant fare increase - consider alternatives'
        ]
      }
    ],
    tips: [
      'Contact passengers before approving major changes',
      'Check for better alternatives on competing airlines'
    ]
  },

  '/automation/ticketing': {
    title: 'Ticketing Queue',
    summary: 'Monitor PNRs ready for ticket issuance. Track payment status, auto-issue eligibility, and QC status.',
    sections: [
      {
        title: 'Ticketing Workflow',
        content: 'Items in this queue have been paid and are awaiting ticket issuance.',
        steps: [
          'Verify payment status is confirmed',
          'Check risk score - high scores may require manual review',
          'Auto-issue eligible items will be ticketed automatically at the scheduled time',
          'Manual items require you to issue tickets via Sabre'
        ]
      },
      {
        title: 'Quality Control',
        content: 'QC status indicates whether the booking has been reviewed for accuracy.',
        steps: [
          'Pending: Needs QC review before ticketing',
          'Passed: QC approved, ready to ticket',
          'Failed: Issues found, requires correction'
        ]
      }
    ],
    relatedPages: [
      { name: 'PNT Tracking', path: '/pnt' },
      { name: 'Payments', path: '/payments' }
    ]
  },

  '/operations/queue': {
    title: 'Smart Queue',
    summary: 'Unified task command center aggregating all task types in one view. Claim and resolve tasks efficiently.',
    sections: [
      {
        title: 'Task Types',
        content: 'The Smart Queue combines tasks from multiple sources.',
        steps: [
          'Booking Requests: New booking inquiries to process',
          'Schedule Changes: Flight changes requiring action',
          'Ticketing Deadlines: PNRs approaching TTL',
          'VIP Requests: Priority customer tasks'
        ]
      },
      {
        title: 'Working Tasks',
        content: 'Claim a task to assign it to yourself, then resolve when complete.',
        steps: [
          'Click "Claim" to take ownership of a task',
          'Complete the required action in Sabre or other systems',
          'Click "Resolve" to mark as completed',
          'Add notes if needed for future reference'
        ]
      }
    ],
    tips: [
      'Sort by deadline to see most urgent tasks first',
      'Filter by "Assigned to Me" to see your current workload'
    ]
  },

  // ==================== BOOKINGS ====================
  '/search': {
    title: 'Flight Search',
    summary: 'Search for available flights across GDS and consolidator sources. Compare prices and book directly.',
    sections: [
      {
        title: 'Basic Search',
        content: 'Enter your travel requirements to find available flights.',
        steps: [
          'Enter origin and destination airports (3-letter codes)',
          'Select departure and return dates',
          'Specify number of passengers by type (Adult, Child, Infant)',
          'Choose cabin class (Economy, Business, First)',
          'Click Search to find flights'
        ]
      },
      {
        title: 'Understanding Results',
        content: 'Results show flights from multiple sources with pricing.',
        steps: [
          'Flights are sorted by price by default',
          'Filter by airline, stops, departure time',
          'Click on a fare to see full pricing breakdown',
          'Select to proceed to booking'
        ]
      }
    ],
    relatedPages: [
      { name: 'Price Compare', path: '/price-comparison' },
      { name: 'Quick Book', path: '/quick-book' }
    ]
  },

  '/consolidators': {
    title: 'Agentic Fare Search',
    summary: 'Advanced AI-powered search that automatically queries multiple consolidator portals to find the best net fares.',
    sections: [
      {
        title: 'How It Works',
        content: 'The system uses browser automation to search consolidator websites and return best fares.',
        steps: [
          'Enter your search criteria',
          'System automatically searches AerTicket, Picasso, and other sources',
          'Results are aggregated and compared',
          'Best options are highlighted'
        ]
      },
      {
        title: 'Consolidator Sources',
        content: 'Searches across configured consolidator partners.',
        steps: [
          'AerTicket UK: European consolidator fares',
          'Picasso US: North American consolidator fares',
          'Direct GDS: Published fares for comparison'
        ]
      }
    ],
    tips: [
      'Allow 30-60 seconds for consolidator searches to complete',
      'Check BSP fare rules when comparing with consolidator fares'
    ]
  },

  '/quick-book': {
    title: 'Quick Book',
    summary: 'Paste an itinerary or PNR and quickly create a booking. Ideal for converting email quotes into bookings.',
    sections: [
      {
        title: 'Pasting an Itinerary',
        content: 'Copy and paste flight details from emails, websites, or other sources.',
        steps: [
          'Paste the itinerary text into the input area',
          'System automatically parses flight details',
          'Review and correct any parsing errors',
          'Add passenger details',
          'Proceed to booking'
        ]
      },
      {
        title: 'Supported Formats',
        content: 'The parser recognizes various itinerary formats.',
        steps: [
          'GDS cryptic formats (Sabre, Amadeus)',
          'Email itinerary text',
          'Website booking confirmations',
          'Manual entry also supported'
        ]
      }
    ]
  },

  '/bookings': {
    title: 'All Bookings',
    summary: 'View and manage all bookings in the system. Filter, search, and access booking details.',
    sections: [
      {
        title: 'Finding Bookings',
        content: 'Search and filter to find specific bookings.',
        steps: [
          'Search by PNR, passenger name, or reference',
          'Filter by date range, status, or agent',
          'Sort by departure date, booking date, or value',
          'Click a booking to view full details'
        ]
      },
      {
        title: 'Booking Actions',
        content: 'Available actions depend on booking status.',
        steps: [
          'View: See full booking details and history',
          'Edit: Modify passenger or contact information',
          'Cancel: Request cancellation and refund',
          'Sync: Refresh data from GDS'
        ]
      }
    ]
  },

  '/air/itinerary-composer': {
    title: 'Client Air Itinerary',
    summary: 'Review pasted air segments and copy a private-data-safe itinerary for a client email.',
    sections: [
      {
        title: 'Build from Sabre text',
        content: 'Paste Sabre air-segment lines and choose the journey year. The composer never guesses a year.',
        steps: [
          'Paste the complete air itinerary',
          'Choose the journey year',
          'Review every parsed and unresolved flight line',
          'Correct missing details before copying'
        ]
      },
      {
        title: 'Copy for email',
        content: 'Copy formatted HTML with equivalent plain text. Booking status is hidden unless you explicitly include it.',
        steps: [
          'Check marketing and operating carriers',
          'Leave booking status hidden for ordinary client copy',
          'Select Show booking status only when needed',
          'Copy and paste into your email editor'
        ]
      }
    ],
    tips: [
      'Copying does not send an email, create a draft, or update a provider.',
      'Unresolved flight lines block copying so incomplete output cannot look complete.'
    ]
  },

  '/pnr': {
    title: 'PNR Manager',
    summary: 'Advanced PNR management tool. Retrieve, view, modify, and sync PNRs with the GDS.',
    sections: [
      {
        title: 'Retrieving a PNR',
        content: 'Enter a 6-character record locator to retrieve PNR details.',
        steps: [
          'Enter the PNR (e.g., ABC123)',
          'Click Retrieve or press Enter',
          'System fetches latest data from Sabre',
          'Review all segments, passengers, and remarks'
        ]
      },
      {
        title: 'PNR Timeline',
        content: 'View the chronological history of changes made to the PNR.'
      }
    ],
    relatedPages: [
      { name: 'PNR History', path: '/pnr/history' },
      { name: 'PNT Tracking', path: '/pnt' }
    ]
  },

  // ==================== CUSTOMER HUB ====================
  '/customers': {
    title: 'Customer Database',
    summary: 'Manage your customer relationships. View profiles, booking history, preferences, and communication records.',
    sections: [
      {
        title: 'Customer Profiles',
        content: 'Each customer has a comprehensive profile with all relevant information.',
        steps: [
          'View contact details and preferences',
          'See booking history and upcoming trips',
          'Track interactions and communications',
          'Manage documents and travel preferences'
        ]
      },
      {
        title: 'Adding Customers',
        content: 'Create new customer records manually or from booking imports.',
        steps: [
          'Click "Add Customer" button',
          'Enter basic details (name, email, phone)',
          'Add additional information as available',
          'Link to company if applicable'
        ]
      }
    ],
    relatedPages: [
      { name: 'Companies', path: '/companies' },
      { name: 'Traveler Profiles', path: '/travelers' }
    ]
  },

  '/companies': {
    title: 'Companies',
    summary: 'Manage corporate accounts and company relationships. Track contracts, policies, and associated travelers.',
    sections: [
      {
        title: 'Company Management',
        content: 'Each company record contains business relationship details.',
        steps: [
          'View company profile and contacts',
          'Manage associated travelers',
          'Track booking volume and spend',
          'Configure travel policies and approvals'
        ]
      }
    ]
  },

  '/travelers': {
    title: 'Traveler Profiles',
    summary: 'Manage detailed traveler profiles including passport details, preferences, and loyalty programs.',
    sections: [
      {
        title: 'Profile Information',
        content: 'Traveler profiles store information needed for booking and travel.',
        steps: [
          'Personal details and contact information',
          'Passport and visa details',
          'Frequent flyer numbers',
          'Seat and meal preferences',
          'Special requirements'
        ]
      },
      {
        title: 'Auto-fill Feature',
        content: 'Traveler details can automatically populate booking forms.'
      }
    ]
  },

  // ==================== FINANCE ====================
  '/finance': {
    title: 'Finance Overview',
    summary: 'Financial dashboard showing key metrics, outstanding balances, and recent transactions.',
    sections: [
      {
        title: 'Key Metrics',
        content: 'Overview of your financial position.',
        steps: [
          'Total revenue this period',
          'Outstanding receivables',
          'Pending supplier payments',
          'Cash flow projection'
        ]
      }
    ],
    relatedPages: [
      { name: 'Invoices', path: '/invoices' },
      { name: 'Payments', path: '/payments' },
      { name: 'Cash Flow', path: '/finance/cashflow' }
    ]
  },

  '/invoices': {
    title: 'Invoices',
    summary: 'Create, manage, and send invoices to customers. Track payment status and send reminders.',
    sections: [
      {
        title: 'Creating Invoices',
        content: 'Generate invoices from bookings or create manually.',
        steps: [
          'Click "New Invoice" or generate from booking',
          'Select customer and add line items',
          'Apply any discounts or adjustments',
          'Preview and send to customer'
        ]
      },
      {
        title: 'Payment Tracking',
        content: 'Monitor invoice status and follow up on overdue payments.',
        steps: [
          'Draft: Not yet sent',
          'Sent: Awaiting payment',
          'Paid: Payment received',
          'Overdue: Past due date'
        ]
      }
    ]
  },

  '/payments': {
    title: 'Payments',
    summary: 'Record and track customer payments. Process refunds and manage payment allocations.',
    sections: [
      {
        title: 'Recording Payments',
        content: 'Record payments received from customers.',
        steps: [
          'Select the customer',
          'Enter payment amount and method',
          'Allocate to invoices',
          'Save payment record'
        ]
      }
    ]
  },

  '/finance/cashflow': {
    title: 'Cash Flow Projection',
    summary: 'Forecast cash flow based on PNT due dates and expected payments. Plan your financial position.',
    sections: [
      {
        title: 'Understanding Projections',
        content: 'Cash flow projections help you plan for upcoming financial obligations.',
        steps: [
          'Expected Inflows: Payments due from customers',
          'Expected Outflows: Supplier payments due',
          'Net Cash Flow: Projected balance'
        ]
      },
      {
        title: 'PNT Integration',
        content: `${PNT_LONG} bookings are included in projections based on ticketing deadlines.`
      }
    ]
  },

  '/pnt': {
    title: PNT_LONG,
    summary: `${PNT_DESCRIPTION} Monitor ticketing deadlines to avoid losing bookings.`,
    sections: [
      {
        title: 'PNT Workflow',
        content: 'PNT items require attention before their ticketing deadline.',
        steps: [
          'Review payment confirmation',
          'Verify passenger details are correct',
          'Check fare rules for any restrictions',
          'Issue ticket before TTL expires'
        ]
      },
      {
        title: 'Deadline Alerts',
        content: 'System alerts you when deadlines are approaching.',
        steps: [
          'Red: Deadline within 24 hours',
          'Yellow: Deadline within 72 hours',
          'Green: Deadline more than 72 hours away'
        ]
      }
    ],
    tips: [
      'Review PNT list daily',
      'Set up automatic reminders for approaching deadlines'
    ]
  },

  '/finance/supplier-invoices': {
    title: 'Supplier Invoices',
    summary: 'Upload and process supplier invoices including AerTicket UK statements. Automatic matching to bookings.',
    sections: [
      {
        title: 'Uploading Invoices',
        content: 'Upload PDF invoices from consolidators for automatic processing.',
        steps: [
          'Click "Upload Invoice"',
          'Select the supplier (e.g., AerTicket UK)',
          'Upload the PDF file',
          'System extracts and matches line items'
        ]
      },
      {
        title: 'Reconciliation',
        content: 'Review matched items and resolve any discrepancies.'
      }
    ],
    relatedPages: [
      { name: 'Statement Reconciliation', path: '/finance/statement-reconciliation' }
    ]
  },

  '/finance/statement-reconciliation': {
    title: 'Statement Reconciliation',
    summary: 'Reconcile supplier statements with your booking records. Identify discrepancies and track payment obligations.',
    sections: [
      {
        title: 'Reconciliation Process',
        content: 'Upload statements and reconcile with your records.',
        steps: [
          'Upload the weekly statement',
          'System matches to existing bookings',
          'Review any unmatched items',
          'Mark statement as reconciled'
        ]
      },
      {
        title: 'Payment Tracking',
        content: 'Track payment obligations based on supplier billing cycles.',
        steps: [
          'AerTicket UK: Friday-Thursday cycle, payment due following Friday'
        ]
      }
    ]
  },

  // ==================== TOOLS ====================
  '/interface-import': {
    title: 'Interface Import',
    summary: 'Import Sabre IUR and Amadeus AIR interface files to populate customer trips and tracking data.',
    sections: [
      {
        title: 'Uploading Files',
        content: 'Upload interface files from your back-office system.',
        steps: [
          'Select file type (IUR or AIR)',
          'Choose the file to upload',
          'System parses and extracts bookings',
          'Review imported data and PNR links'
        ]
      },
      {
        title: 'File Types',
        content: 'Supported interface file formats.',
        steps: [
          'IUR (Sabre): Interface Universal Record - ticketing and segments',
          'AIR (Amadeus/Sabre): Air Interface Record - detailed segment info'
        ]
      }
    ],
    tips: [
      'Import files regularly to keep tracking data current',
      'Check PNR links after import to ensure proper association'
    ]
  },

  '/sabre/terminal': {
    title: 'Cryptic Terminal',
    summary: 'Direct access to Sabre cryptic command interface. Execute GDS commands and view responses.',
    sections: [
      {
        title: 'Common Commands',
        content: 'Frequently used Sabre cryptic commands.',
        steps: [
          '*ABC123 - Retrieve PNR',
          'QC/ - Queue count',
          'QS/1 - Sign onto queue 1',
          'QXI - Exit queue without removing',
          'W#PQ1 - Price itinerary'
        ]
      },
      {
        title: 'Command History',
        content: 'Use up/down arrows to navigate previous commands.'
      }
    ],
    tips: [
      'Commands are case-insensitive',
      'Use IR to ignore and redisplay PNR'
    ]
  },

  // ==================== AI TOOLS ====================
  '/ai-assistant': {
    title: 'AI Assistant',
    summary: 'Chat with the AI assistant for help with bookings, fare queries, and platform guidance.',
    sections: [
      {
        title: 'What AI Can Help With',
        content: 'The assistant can help with various tasks.',
        steps: [
          'Answer questions about fares and routes',
          'Help interpret PNR data',
          'Provide guidance on platform features',
          'Assist with complex itinerary planning'
        ]
      }
    ]
  },

  // ==================== SETTINGS ====================
  '/settings': {
    title: 'User Preferences',
    summary: 'Configure your personal settings including display preferences, notifications, and defaults.',
    sections: [
      {
        title: 'Available Settings',
        content: 'Customize your experience.',
        steps: [
          'Default PCC and sign-in credentials',
          'Notification preferences',
          'Display density and theme',
          'Keyboard shortcut customization'
        ]
      }
    ]
  },

  '/settings/gds': {
    title: 'GDS Configuration',
    summary: 'Configure your GDS credentials for Sabre and Amadeus connections.',
    sections: [
      {
        title: 'Sabre Configuration',
        content: 'Set up your Sabre API credentials.',
        steps: [
          'Enter your EPR (Employee Profile Record)',
          'Add password and PCC',
          'Select environment (Cert or Production)',
          'Test connection to verify'
        ]
      }
    ],
    tips: [
      'Use Cert environment for testing',
      'Keep credentials secure and don\'t share'
    ]
  },

  // ==================== TRAVEL PLANNING ====================
  '/rtw': {
    title: 'RTW Planner',
    summary: 'Plan round-the-world itineraries using oneworld explorer fares. Build complex multi-city routes.',
    sections: [
      {
        title: 'Building an RTW',
        content: 'Create round-the-world itineraries step by step.',
        steps: [
          'Select starting city',
          'Add stops in your desired order',
          'System calculates mileage and fare zone',
          'Adjust dates and routing',
          'Generate pricing'
        ]
      },
      {
        title: 'Fare Rules',
        content: 'oneworld Explorer fare rules govern RTW itineraries.',
        steps: [
          'Must travel in one direction (E or W)',
          'Maximum 16 segments',
          'Up to 5 surface sectors allowed',
          'Must return to origin country'
        ]
      }
    ]
  },

  '/loyalty': {
    title: 'Loyalty Calculator',
    summary: 'Calculate tier points and miles for flights across various airline loyalty programs.',
    sections: [
      {
        title: 'Calculating Points',
        content: 'Enter flight details to calculate earning.',
        steps: [
          'Select frequent flyer program',
          'Enter flight route and class',
          'Choose ticket fare basis',
          'View calculated tier points and miles'
        ]
      }
    ]
  },

  // ==================== ADMIN ====================
  '/admin/users': {
    title: 'User Management',
    summary: 'Manage platform users, roles, and permissions. Add new users and configure access levels.',
    sections: [
      {
        title: 'Managing Users',
        content: 'Control who has access to the platform.',
        steps: [
          'View all users and their roles',
          'Invite new users via email',
          'Assign roles (Agent, Admin, Super Admin)',
          'Deactivate users when needed'
        ]
      }
    ]
  },

  '/admin/permissions': {
    title: 'Permission Center',
    summary: 'Configure role-based permissions and access controls for the platform.',
    sections: [
      {
        title: 'Role Permissions',
        content: 'Define what each role can access and modify.',
        steps: [
          'View permissions by role',
          'Customize access levels',
          'Create custom roles if needed'
        ]
      }
    ]
  },

  // ==================== INTEGRATIONS ====================
  '/freshdesk': {
    title: 'Freshdesk Dashboard',
    summary: 'Review mirrored Freshdesk ticket counts, sync status, and recent activity.',
    sections: [
      {
        title: 'Mirror overview',
        content: 'Use the mirrored counts for an accurate open and pending overview, then open the customer requests inbox for full customer and case context.',
        steps: [
          'Review the open, pending, resolved, closed, and hold counts',
          'Check when the Freshdesk mirror last synced',
          'Open Freshdesk ticket sync to populate the mirror when it has never synced',
          'Open the customer requests inbox to work on customer responses'
        ]
      }
    ]
  },

  '/integrations/todoist': {
    title: 'Todoist Integration',
    summary: 'Connect Todoist for task management. Sync queue items and reminders with your Todoist projects.',
    sections: [
      {
        title: 'Setting Up Integration',
        content: 'Connect your Todoist account.',
        steps: [
          'Click "Connect to Todoist"',
          'Authorize access',
          'Select project for tasks',
          'Configure sync preferences'
        ]
      }
    ]
  },

  '/admin/delivery': {
    title: 'Delivery & Decisions',
    summary: 'Review live release trains, the 47-item delivery plan, and open operational decision cards.',
    sections: [
      {
        title: 'Answering Decision Cards',
        content: 'Each decision card explains the operational question, safe default, and impact before any switch is changed.',
        steps: [
          'Review the open decision cards at the top of the page',
          'Select your preferred option or record a note',
          'Inspect the release train ledger and test evidence below'
        ]
      }
    ],
    relatedPages: [
      { name: 'System Manual', path: '/admin/system-manual' },
      { name: 'Automations', path: '/admin/automations' }
    ]
  },

  '/settings/morning-message': {
    title: 'Morning Message Settings',
    summary: 'Configure who receives a personal Slack work summary each working morning, delivery times, and message history.',
    sections: [
      {
        title: 'Managing Daily Slack Summaries',
        content: 'Approved staff can view their own delivered morning messages; administrators can configure recipients and schedule times.',
        steps: [
          'Check recipient toggles and UK delivery time',
          'Preview what is waiting in each person\'s morning summary',
          'Inspect recent delivery logs and Slack status'
        ]
      }
    ],
    relatedPages: [
      { name: 'Settings', path: '/settings' },
      { name: 'Today Board', path: '/ops/today' }
    ]
  },

  '/settings/api-access': {
    title: 'API Access Tokens',
    summary: 'Generate and revoke scoped personal access tokens for Claude, ChatGPT, and external automation scripts.',
    sections: [
      {
        title: 'Token Governance',
        content: 'Tokens inherit your staff permissions and can be revoked immediately at any time.',
        steps: [
          'Click "Create Token" and assign a clear label',
          'Copy the secret token once (it is never shown again in plaintext)',
          'Revoke unused or expired tokens immediately'
        ]
      }
    ],
    relatedPages: [
      { name: 'Integrations Hub', path: '/integrations' },
      { name: 'Permission Center', path: '/admin/permissions' }
    ]
  },

  '/settings/connections': {
    title: 'Connections & Integrations',
    summary: 'Manage connected accounts, Gmail OAuth tokens, and third-party service webhooks.',
    sections: [
      {
        title: 'Checking Connection Health',
        content: 'Verify that OAuth tokens and API keys are active across all 5 credential stores.',
        steps: [
          'Review active vs. expired connection badges',
          'Reconnect OAuth providers when tokens expire',
          'Open the central Integrations Hub for cross-system diagnostics'
        ]
      }
    ],
    relatedPages: [
      { name: 'Integrations Hub', path: '/integrations' },
      { name: 'System Health', path: '/admin/system-health' }
    ]
  },

  '/settings/automations': {
    title: 'Automation Settings',
    summary: 'Configure background workflow switches, client notification gates, and ticketing rules.',
    sections: [
      {
        title: 'Safety Switches',
        content: 'Customer-facing senders and automated decisions remain off by default until explicitly enabled.',
        steps: [
          'Review each automation switch and its current mode',
          'Enter a reason before saving any setting change',
          'Audit recent configuration changes in the history log'
        ]
      }
    ],
    relatedPages: [
      { name: 'Automation Control Center', path: '/admin/automations' },
      { name: 'Delivery & Decisions', path: '/admin/delivery' }
    ]
  },

  '/ops/customer-requests': {
    title: OPS_SURFACE_LABELS['customer-requests'].label,
    summary: 'Triage post-booking customer care requests, special assistance, and service recovery cases.',
    sections: [
      {
        title: 'Handling customer requests',
        content: 'Track special meals, wheelchair assistance, seating, and service escalations to completion.',
        steps: [
          'Review open care requests by departure urgency',
          'Verify SSR remarks in the linked Sabre/Amadeus PNR',
          'Confirm completion with the customer in UCC'
        ]
      }
    ],
    relatedPages: [
      { name: 'AOC Control Tower', path: '/aoc' },
      { name: 'Trips', path: '/trips' }
    ]
  },

  '/pnt/analytics': {
    title: 'PNT Analytics & Auto-Decisions',
    summary: 'Analyse paid-not-ticketed trends, review supplier documents, and manage governed automatic PNT decisions.',
    sections: [
      {
        title: 'Reviewing Automatic Decisions & Supplier Docs',
        content: 'Inspect proposed automatic decisions (such as flown coupons still marked OPEN) and bulk supplier document matches.',
        steps: [
          'Review the urgency breakdown and open currency figures',
          'Inspect or undo governed automatic PNT decisions',
          'Use the Supplier Document Review Panel to match consolidator statements'
        ]
      }
    ],
    relatedPages: [
      { name: 'PNT Workbench', path: '/pnt' },
      { name: 'Supplier Invoices', path: '/finance/supplier-invoices' }
    ]
  },

  '/pnt/outlook': {
    title: 'PNT Daily Outlook',
    summary: 'Day-by-day calendar view of ticketing deadlines, payment follow-ups, and upcoming departures.',
    sections: [
      {
        title: 'Daily Ticketing Triage',
        content: 'Focus on tickets expiring today and within the next 72 hours.',
        steps: [
          'Check today\'s critical TTL items first',
          'Open the PNR or UCC thread directly from any row',
          'Record a decision in the PNT workbench once actioned'
        ]
      }
    ],
    relatedPages: [
      { name: 'PNT Workbench', path: '/pnt' },
      { name: 'Ticketing Deadlines', path: '/bookings/deadlines' }
    ]
  },

  '/admin/rtw-fare-rules': {
    title: 'RTW Filed Fare Rules (Rule 3015)',
    summary: 'Inspect the canonical oneworld Explorer (Rule 3015) and Global Explorer filed fare rule clauses and validator mappings.',
    sections: [
      {
        title: 'Auditing Rule Clauses',
        content: 'Every RTW validation check is backed by a specific filed tariff paragraph and versioned snapshot.',
        steps: [
          'Search by rule code, continent zone, or stopover clause',
          'Compare filed tariff text against the validator logic',
          'Open the RTW Planner to test an itinerary against the rules'
        ]
      }
    ],
    relatedPages: [
      { name: 'RTW Planner', path: '/rtw' },
      { name: 'RTW AI Assistant', path: '/rtw/assistant' }
    ]
  },

  '/rtw/assistant': {
    title: 'RTW AI Assistant',
    summary: 'Interactive AI specialist for researching complex oneworld Explorer routings, D-class availability strategies, and tier point optimisation.',
    sections: [
      {
        title: 'Using the RTW Assistant',
        content: 'Ask routing questions, paste PNR segments for Rule 3015 diagnosis, or compare BA vs. Finnair tier point strategies.',
        steps: [
          'Describe the client\'s origin, target cities, and status goal',
          'Review suggested continent sequences and stopover checks',
          'Send the route into the RTW Planner to validate and price'
        ]
      }
    ],
    relatedPages: [
      { name: 'RTW Planner', path: '/rtw' },
      { name: 'Tier Points Calculator', path: '/loyalty' }
    ]
  },

  '/crm/segments': {
    title: 'Customer Segments',
    summary: 'Build and review dynamic customer cohorts by VIP tier, spend, loyalty programme, and travel preferences.',
    sections: [
      {
        title: 'Working with Cohorts',
        content: 'Segment customers for targeted white-glove outreach and service reviews.',
        steps: [
          'Select a cohort or create custom filter criteria',
          'Inspect member customers and lifetime spend metrics',
          'Open any customer profile to view full cockpit history'
        ]
      }
    ],
    relatedPages: [
      { name: 'Customer Directory', path: '/crm/customers' },
      { name: 'CRM Workspace', path: '/crm' }
    ]
  },

  '/crm/leads': {
    title: 'Leads Pipeline',
    summary: 'Track inbound travel enquiries, qualification scores, and conversion into active CRM customers and quotes.',
    sections: [
      {
        title: 'Lead Progression',
        content: 'Move prospective clients from initial enquiry through consultation and proposal to booked trip.',
        steps: [
          'Review new enquiries and lead qualification scores',
          'Schedule or link a Calendly consultation',
          'Convert qualified leads into a CRM customer and RTW/trip quote'
        ]
      }
    ],
    relatedPages: [
      { name: 'CRM Workspace', path: '/crm' },
      { name: 'RTW Planner', path: '/rtw' }
    ]
  }
};
